import { FC, Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import BasicModal from '__components/Modals/presets/BasicModal';
import LongText from '__components/Inputs/LongText';
import TextInput from '__components/Inputs/TextInput';
import { useToast } from '__components/ToastProvider';
import { ClipboardButton } from 'ts-components/ClipboardButton';
import { KIZEN_SETUP_COMMANDS, KIZEN_SETUP_PROMPT } from '../constants';

const PROMPT_HEIGHT = 140;

const StyledCopyField = styled.div`
  position: relative;

  // Disabled TextInput renders its value as a <p>
  p {
    padding-right: 32px;
  }
`;

const StyledCopyButton = styled(ClipboardButton)`
  position: absolute;
  right: 8px;
  bottom: 0;
  display: flex;
  align-items: center;
  height: 36px;
`;

const CopyField: FC<{ value: string; children: ReactNode }> = ({
  value,
  children,
}) => (
  <StyledCopyField>
    {children}
    <StyledCopyButton value={value} />
  </StyledCopyField>
);

export const CreateBusinessAppModal: FC<{ onHide: () => void }> = ({
  onHide,
}) => {
  const { t } = useTranslation();
  const [showToast, toastVariant] = useToast();
  const [showManualSetup, setShowManualSetup] = useState(false);

  const prompt = KIZEN_SETUP_PROMPT;
  const commands = [
    {
      key: 'login',
      label: t('Sign in'),
      value: KIZEN_SETUP_COMMANDS.login,
    },
    {
      key: 'connect',
      label: t('Connect Kizen to your agent'),
      value: KIZEN_SETUP_COMMANDS.connect,
    },
  ];

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      showToast({
        message: t("Couldn't copy the prompt. Select it and copy it instead."),
        variant: toastVariant.FAILURE,
      });
      return;
    }
    showToast({
      message: t('Prompt copied. Paste it into your coding agent.'),
      variant: toastVariant.SUCCESS,
    });
    onHide();
  };

  return (
    <BasicModal
      heading={t('Create Business App')}
      buttonText={t('Copy Prompt')}
      onConfirm={copyPrompt}
      onHide={onHide}
      show
      size="small"
    >
      <Typography>
        {t(
          'Paste this prompt into your coding agent. It runs the Kizen CLI, signs you in and connects Kizen.'
        )}
      </Typography>
      <Spacer size={15} />
      <LongText
        label={t('Setup Prompt')}
        value={prompt}
        disabled
        enableResize={false}
        fixedHeight={PROMPT_HEIGHT}
        fullHeight
        data-qa-id="create-business-app-prompt"
      />
      <div className="mt-[10px]">
        <Button
          variant="text"
          color="secondary"
          preserveCase
          leftIcon={
            showManualSetup ? 'custom-chevron-up' : 'custom-chevron-down'
          }
          onClick={() => setShowManualSetup((show) => !show)}
          qa={{ id: 'create-business-app-manual-setup-toggle' }}
        >
          {t('Set it up manually')}
        </Button>
      </div>
      {showManualSetup
        ? commands.map((command) => (
            <Fragment key={command.key}>
              <Spacer size={15} />
              <CopyField value={command.value}>
                <TextInput
                  label={command.label}
                  value={command.value}
                  disabled
                  data-qa-id={`create-business-app-${command.key}-command`}
                />
              </CopyField>
            </Fragment>
          ))
        : null}
    </BasicModal>
  );
};
